// components/landing/LandingPage.tsx
// ---------------------------------------------------------------------------
// Main landing page orchestrator with scroll-snap between hero and content.
//
// Section 1 (100vh): Hero with video background, centered card, stats
// Section 2 (scrollable): Security, Partners, Features, How It Works,
//                          Pricing, Footer
//
// The snap transition ONLY applies between hero (section 1) and the rest
// (section 2). Within section 2, all content scrolls normally. Scrolling
// back up from the top of section 2 snaps back to the hero.
// ---------------------------------------------------------------------------

import { Box, alpha, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SEOHead from "../common/SEOHead";
import LandingNavBar from "./LandingNavBar";
import { athenaColors } from "./colors";
import FeatureCards from "./sections/FeatureCards";
import HeroSection from "./sections/HeroSection";
import HowItWorks from "./sections/HowItWorks";
import LandingFooter from "./sections/LandingFooter";
// import PricingSection from "./sections/PricingSection"; // hidden per request
import SecurityBanner from "./sections/SecurityBanner";
import TrustedBy from "./sections/TrustedBy";
import usePageView, { sendPageView } from "../../hooks/usePageView";
import { useSelector } from "react-redux";
import { RootState } from "../../types";

export default function LandingPage() {
  usePageView("/");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navH = (theme.custom?.navbarHeight as number) || 64;
  const [searchParams, setSearchParams] = useSearchParams();
  const [scrolled, setScrolled] = useState(false);
  const authToken = useSelector((state: RootState) => state.auth.token);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  // Auto-show login when redirected from auth failure (?signin=1)
  const [showLogin, setShowLogin] = useState(
    searchParams.get("signin") === "1"
  );

  // When an authenticated waitlisted user lands here (post-OAuth redirect),
  // auto-open the panel so they see the Beta Gate Card immediately.
  useEffect(() => {
    if (currentUser?.access_tier === "waitlisted") {
      setShowLogin(true);
    }
  }, [currentUser?.access_tier]);

  // Show SSO buttons unless: user is authenticated AND explicitly waitlisted.
  // Unauthenticated users always see SSO (we don't have their email yet).
  // System admins and beta users always bypass regardless of access_tier.
  const bypassBetaGate =
    searchParams.get("beta") === "1" ||
    !currentUser ||
    currentUser.access_tier !== "waitlisted" ||
    currentUser.is_system_admin ||
    currentUser.is_beta_user;
  const secondSectionRef = useRef<HTMLDivElement>(null);

  // Clean up the query param once consumed so the URL stays clean
  useEffect(() => {
    if (searchParams.get("signin") === "1") {
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track sign-in when arriving via ?signin=1
  useEffect(() => {
    if (showLogin) sendPageView("/signin", authToken);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sign In: show login form in hero card
  const handleSignIn = () => {
    if (isMobile) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (scrolled) {
      setScrolled(false);
    }
    setShowLogin(true);
    sendPageView("/signin", authToken);
  };

  const handleBackFromLogin = () => {
    setShowLogin(false);
  };

  // Learn More: snap to section 2 (desktop) or smooth scroll (mobile)
  const handleLearnMore = () => {
    setShowLogin(false);
    if (isMobile) {
      window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
    } else {
      setScrolled(true);
    }
  };

  // Nav anchor click (Product, Pricing, Team, Contact)
  const handleNavClick = (href: string) => {
    if (!href.startsWith("#")) return;
    setShowLogin(false);
    const sectionId = href.slice(1);

    if (isMobile) {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: "smooth" });
      return;
    }

    if (!scrolled) {
      setScrolled(true);
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 800);
    } else {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Scroll-snap: wheel handler (desktop only, disabled on mobile for reliability)
  useEffect(() => {
    if (isMobile) return;

    const handleWheel = (e: WheelEvent) => {
      if (!scrolled && e.deltaY > 30) {
        setScrolled(true);
      } else if (scrolled && e.deltaY < -30) {
        const el = secondSectionRef.current;
        if (el && el.scrollTop <= 0) {
          setScrolled(false);
        }
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: true });
    return () => {
      window.removeEventListener("wheel", handleWheel);
    };
  }, [scrolled, isMobile]);

  // On mobile: natural scroll (no transform snap).
  // On desktop: two-panel 200vh with transform snap.
  if (isMobile) {
    return (
      <Box
        sx={{
          width: "100vw",
          overflowX: "hidden",
          backgroundColor: athenaColors.deepNavy,
        }}
      >
        <SEOHead
          title="Athena OncoCraft — Clinical Intelligence Platform"
          description="Athena OncoCraft transforms unstructured clinical data into structured, queryable intelligence across 44 cancer types. Agentic extraction, natural language queries, and real-time cohort analytics for hospitals, CROs, and research consortiums."
          path="/"
          fullTitle
        />
        <LandingNavBar onSignIn={handleSignIn} onNavClick={handleNavClick} />

        <Box sx={{ minHeight: "100vh" }}>
          <HeroSection
            showLogin={showLogin}
            onSignIn={handleSignIn}
            onBackFromLogin={handleBackFromLogin}
            onLearnMore={handleLearnMore}
            bypassBetaGate={bypassBetaGate}
          />
        </Box>

        <Box
          sx={{
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Box
            sx={{
              minHeight: "100vh",
              display: "flex",
              flexDirection: "column",
              pt: `${navH}px`,
              background: `linear-gradient(135deg, ${athenaColors.deepNavy} 0%, ${alpha(athenaColors.royalBlue, 0.95)} 100%)`,
            }}
          >
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <SecurityBanner />
            </Box>
            <TrustedBy />
          </Box>
          <FeatureCards />
          <HowItWorks />
          {/* Pricing section hidden per request */}
          {/* <PricingSection /> */}
          <LandingFooter />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        position: "relative",
        backgroundColor: athenaColors.deepNavy,
      }}
    >
      <SEOHead
        title="AI-Powered Oncology Data Platform"
        description="Athena Tech transforms unstructured clinical data into structured, queryable intelligence across 44 cancer types. AI-powered extraction, natural language queries, and real-time cohort analytics for hospitals, CROs, and research consortiums."
        path="/"
        fullTitle
      />
      <LandingNavBar onSignIn={handleSignIn} onNavClick={handleNavClick} />

      <Box
        sx={{
          height: "200vh",
          transform: `translateY(${scrolled ? "-100vh" : "0"})`,
          transition: "transform 0.7s cubic-bezier(0.65, 0, 0.35, 1)",
        }}
      >
        {/* Section 1: Hero (100vh, no internal scroll) */}
        <Box sx={{ height: "100vh" }}>
          <HeroSection
            showLogin={showLogin}
            onSignIn={handleSignIn}
            onBackFromLogin={handleBackFromLogin}
            onLearnMore={handleLearnMore}
            bypassBetaGate={bypassBetaGate}
          />
        </Box>

        {/* Section 2: Rest of page (security + trust first, then features) */}
        <Box
          ref={secondSectionRef}
          sx={{
            height: "100vh",
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Box
            sx={{
              minHeight: "100vh",
              display: "flex",
              flexDirection: "column",
              pt: `${navH}px`,
              background: `linear-gradient(135deg, ${athenaColors.deepNavy} 0%, ${alpha(athenaColors.royalBlue, 0.95)} 100%)`,
            }}
          >
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <SecurityBanner />
            </Box>
            <TrustedBy />
          </Box>
          <FeatureCards />
          <HowItWorks />
          {/* Pricing section hidden per request */}
          {/* <PricingSection /> */}
          <LandingFooter />
        </Box>
      </Box>
    </Box>
  );
}
